<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Modules"
      subtitle="Manage feature modules available to societies."
      :headers="headers"
      :items="modules"
      :loading="loading"
      show-search
      search-placeholder="Search modules..."
      show-add
      add-label="Add Module"
      @add="addModuleDialog = true"
    >
      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="openDelete(item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Add Module Dialog -->
    <PremiumModalForm
      v-model="addModuleDialog"
      title="Add Module"
      submit-text="Save Module"
      cancel-text="Cancel"
      :loading="saving"
      max-width="460"
      @submit="saveModule"
    >
      <div style="display:flex;flex-direction:column;gap:18px;">
        <PremiumInput
          v-model="module.name"
          name="name"
          label="Module Name *"
          placeholder="e.g. Complaints"
          v-validate="'required'"
          :error="errors.first('name') || serverSideErrors.name"
        />
        <PremiumInput
          v-model="module.route"
          name="route"
          label="Route *"
          placeholder="e.g. complaints"
          v-validate="'required'"
          :error="errors.first('route') || serverSideErrors.route"
        />
      </div>
    </PremiumModalForm>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete Module"
      message="Are you sure you want to delete this module?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteModule"
      @cancel="deleteDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumTable from '../common/PremiumTable.vue';
import ConfirmModal from '../common/ConfirmModal.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumActionButton from '../common/PremiumActionButton.vue';
import PremiumInput from '../common/PremiumInput.vue';

export default {
  name: 'ModulesPage',
  components: { PremiumTable, ConfirmModal, PremiumModalForm, PremiumActionButton, PremiumInput },

  data() {
    return {
      loading:         false,
      saving:          false,
      modules:         [],
      addModuleDialog: false,
      deleteDialog:    false,
      delete_id:       null,
      module:          { name: '', route: '' },
      serverSideErrors: {},

      headers: [
        { text: 'Module Name', value: 'name',    width: '40%' },
        { text: 'Route',       value: 'route',   width: '45%' },
        { text: 'Actions',     value: 'actions', width: '15%', sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    getModules() {
      this.loading = true;
      this.$axios.get('superadmin/modules').then(res => {
        this.loading = false;
        if (res.data.status) this.modules = res.data.modules;
        else this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
      }).catch(() => { this.loading = false; });
    },

    closeModuleDialog() {
      this.addModuleDialog = false;
      this.module = { name: '', route: '' };
      this.serverSideErrors = {};
      this.$validator.reset();
    },

    saveModule() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        this.$axios.post('/superadmin/modules', this.module).then(res => {
          this.saving = false;
          const data = res.data;
          if (data.status) {
            this.getModules();
            this.closeModuleDialog();
            this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: 'Module added successfully.' });
          } else if (data.errors) {
            this.serverSideErrors = {};
            data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
          } else {
            this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
          }
        }).catch(() => { this.saving = false; });
      });
    },

    openDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteModule() {
      const id = this.delete_id;
      this.deleteDialog = false;
      this.$axios.delete('superadmin/modules/' + id).then(res => {
        const data = res.data;
        if (data.status) {
          this.modules = this.modules.filter(m => m.id !== id);
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
        }
      });
    },
  },

  mounted() {
    this.getModules();
  },
};
</script>

<style scoped>
.ism-field-group { display: flex; flex-direction: column; gap: 6px; }
.ism-field-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: var(--text-secondary, #64748b);
}
</style>
